import { FormApi } from "final-form";
import { Form, useForm, useFormState } from "react-final-form";
import { useTranslation } from "react-i18next";
import i18next from "src/i18n/config";
import { DangerOutlinedButton, PrimaryOutlinedButton } from "src/ui/shared/CustomButton";
import { TextField } from "src/ui/shared/form/TextField";
import {
    MinimalTable,
    MinimalTableCell,
    MinimalTableContainer,
    MinimalTableHeader,
    MinimalTableHeaderCell,
} from "src/ui/shared/table/MinimalTableComponents";
import { useValidationSchema } from "src/ui/utils/useValidationSchema";
import { z } from "zod";

import { Row } from "@dashboard/shared/styles";
import { Box, Typography, useTheme } from "@mui/material";
import { MinimalTableBody, MinimalTableRow } from "@shared/table/MinimalTableComponents";

import { Connection, Provisioning } from "../../types";

const validationSchema = z.object({
    ssid: z.string(i18next.t("common:validation.fieldRequired")).min(1, i18next.t("common:validation.fieldRequired")),
    password: z.string().optional(),
    description: z.string().optional(),
});

const isConnectionEnabled = (networks: Connection["networks"], ssid: string) => {
    return !!networks?.find((item) => item.ssid === ssid);
};

type FormValues = {
    ssid: string;
    password?: string;
    description?: string;
};

export const WifiConnection = () => {
    const theme = useTheme();
    const { t } = useTranslation(["pages", "common"]);
    const validate = useValidationSchema(validationSchema);

    const parentForm = useForm();
    const { values: parentFormValues } = useFormState<Partial<Provisioning>>();
    const wifiConectivityEnabled = parentFormValues?.settings?.enableWifi;
    const internetConfig = parentFormValues?.settings?.internetConfig || [];
    const wifiConfig = internetConfig.find((item) => item.type === "wifi");
    const networks = wifiConfig?.networks || [];

    const onSubmit = (values: FormValues, form: FormApi<FormValues>) => {
        const connectionExist = isConnectionEnabled(networks, values.ssid);
        if (!connectionExist) {
            parentForm.change(
                "settings.internetConfig",
                internetConfig.map((item) => (item.type === "wifi" ? { type: "wifi", networks: [...networks, values] } : item)),
            );
            form.restart();
        }
    };

    const onDelete = (ssid: string) => {
        const newNetworks = networks.filter((item) => item.ssid !== ssid);
        const newInternetConfig: Connection[] = internetConfig.map((item) =>
            item.type === "wifi" ? { type: "wifi", networks: newNetworks } : item,
        );

        parentForm.change("settings.internetConfig", newInternetConfig);
    };

    return (
        <Form<FormValues>
            onSubmit={onSubmit}
            validate={validate}
            render={({ form, pristine }) => (
                <Box style={{ marginTop: theme.spacing(4), display: "flex", flexDirection: "column", gap: theme.spacing(4) }}>
                    {!wifiConectivityEnabled ? (
                        <Typography color="error" variant="body2">
                            {t("pages:provisioningPage.alert.wifiRequired")}
                        </Typography>
                    ) : null}
                    <MinimalTableContainer>
                        <MinimalTable>
                            <MinimalTableHeader>
                                <MinimalTableRow>
                                    <MinimalTableHeaderCell align="left">
                                        <Typography fontWeight="bold">{t("pages:provisioningPage.table.header.ssid")}</Typography>
                                    </MinimalTableHeaderCell>
                                    <MinimalTableHeaderCell align="left">
                                        <Typography fontWeight="bold">{t("common:label.description")}</Typography>
                                    </MinimalTableHeaderCell>
                                    <MinimalTableHeaderCell align="left">
                                        <Typography fontWeight="bold">{t("common:label.password")}</Typography>
                                    </MinimalTableHeaderCell>
                                    <MinimalTableHeaderCell align="right"></MinimalTableHeaderCell>
                                </MinimalTableRow>
                            </MinimalTableHeader>
                            <MinimalTableBody>
                                {networks.length ? (
                                    networks.map((item) => (
                                        <MinimalTableRow key={item.ssid}>
                                            <MinimalTableCell align="left">
                                                <Typography>{item.ssid}</Typography>
                                            </MinimalTableCell>
                                            <MinimalTableCell align="left">
                                                <Typography>{item.description || "-"}</Typography>
                                            </MinimalTableCell>
                                            <MinimalTableCell align="left">
                                                <Typography>
                                                    {item.password ? "********" : t("pages:provisioningPage.table.noPassword")}
                                                </Typography>
                                            </MinimalTableCell>
                                            <MinimalTableCell align="right">
                                                <DangerOutlinedButton
                                                    onClick={() => onDelete(item.ssid)}
                                                    name="wifi-connection-delete"
                                                >
                                                    <i className="fa-solid fa-trash" />
                                                </DangerOutlinedButton>
                                            </MinimalTableCell>
                                        </MinimalTableRow>
                                    ))
                                ) : (
                                    <MinimalTableRow>
                                        <MinimalTableCell align="center" colSpan={4}>
                                            <Typography>{t("pages:provisioningPage.table.noNetworks")}</Typography>
                                        </MinimalTableCell>
                                    </MinimalTableRow>
                                )}
                            </MinimalTableBody>
                        </MinimalTable>
                    </MinimalTableContainer>
                    <Box>
                        <Row>
                            <TextField name="ssid" label={t("pages:provisioningPage.input.ssid.caption")} fullWidth />
                            <TextField name="password" label={t("common:label.password")} fullWidth />
                        </Row>
                        <Row>
                            <TextField name="description" label={t("common:label.description")} fullWidth />
                            <PrimaryOutlinedButton onClick={form.submit} disabled={pristine} name="wifi-connection-add">
                                {t("common:button.add")}
                            </PrimaryOutlinedButton>
                        </Row>
                    </Box>
                </Box>
            )}
        />
    );
};
